/** @module Auth0SupportedIdps */
/* eslint-disable import/extensions */
import Auth0Utils from './auth0-utils-purejs.mjs';
import Auth0IdentityProvider from './auth0-identity-provider.mjs';
import Auth0IdentityProviderShopify from './auth0-identity-provider-shopify.mjs';
import Auth0IdentityProviderWoot from './auth0-identity-provider-woot.mjs';
import Auth0dIdpConfig from '../config/auth-idp-config.mjs';
import logger from './logging/auth0-logger.mjs';

/**
 * @constructor
 * @description Helper functions related to operating on the list of supported IDPs. IdP availability is automatically filtered based on tenant class.
 * @param {string} tenantOrDomain The shipstation.com signin domain or the tenant (e.g. signin.shipstation.com, signin-stage.shipstation.com, shipstation-stage, etc)
 * @param {boolean} [includeTestIdps=false] Used only by the unit test runner and just exposes some test entires.
 * @param {string} [appLoginHandlerUrl] Required if this object is instantiated in a browser and you intend to call upon the idp.signinWithRedirectHandler() with any of the IdPs contained in this list, otherwise optional
 */
class Auth0SupportedIdps {
  constructor(options) {
    this._idpConfig = new Auth0dIdpConfig(options);
    const resolved = Auth0Utils.tenantOrDomainResolver(options.tenantOrDomain);
    this._domain = resolved.domain.url;
    this._tenant = resolved.tenant.id;
    this._appLoginHandlerUrl = options.appLoginHandlerUrl;
    this._includeTestIdps = options.includeTestIdps;

    // Get tenant-class filtered IdPs from config
    const activeIdpConfigs = this._idpConfig.getActiveIdps();
    const alwaysAvailableIdpConfigs = this._idpConfig.getAlwaysAvailableIdps();

    console.log('Auth0SupportedIdps => active IdPs for tenant class', activeIdpConfigs.map(i => i.id));

    // Build all IdP objects (supportedIdps = all defined IdPs)
    this._supportedIds = this._idpConfig.idps
      .map(idp => this._identityProviderFactory(idp));

    // Build active IdPs (filtered by tenant class)
    this._activeIdps = activeIdpConfigs
      .map(idp => this._identityProviderFactory(idp));
    console.log('Auth0SupportedIdps._activeIdps', this._activeIdps.map(i => i.id));

    // Build always-available IdPs (filtered by tenant class)
    this._alwaysAvailableIdps = alwaysAvailableIdpConfigs
      .map(idp => this._identityProviderFactory(idp));
    console.log('Auth0SupportedIdps._alwaysAvailableIdps', this._alwaysAvailableIdps.map(i => i.id));
  }

  /**
   * An array list of currently supported Identity Providers
   * @readonly
   * @example <caption>An example Identity Provider Entry</caption>
   * {
   *    name: 'Google',
   *    id: 'google-oauth2',
   *    logoUrl: 'https://cdn.shipstation.com/auth0/shipstation/public/images/sso-icon-google.png',
   * }
   */
  get activeIdps() {
    return this._activeIdps;
  }

  /**
   * An array list of Identity Providers that should always be available on Step 1 (Always Available) of progressive auth
   * @readonly
   */
  get alwaysAvailableIdps() {
    return this._alwaysAvailableIdps;
  }

  /**
   * An array list of currently supported Identity Providers
   * @readonly
   * @example <caption>An example Identity Provider Entry</caption>
   * {
   *    name: 'Google',
   *    id: 'google-oauth2',
   *    logoUrl: 'https://cdn.shipstation.com/auth0/shipstation/public/images/sso-icon-google.png',
   * }
   */
  get supportedIdps() {
    return this._supportedIds;
  }

  _identityProviderFactory = (idpConfig) => {
    // always the same for all idps, but they need to have access to it for redirect
    idpConfig.appLoginHandlerUrl = this._appLoginHandlerUrl;
    switch (idpConfig.id) {
      case 'shopify-oauth2':
        return new Auth0IdentityProviderShopify(idpConfig);
      case 'woot-oauth2':
        return new Auth0IdentityProviderWoot(idpConfig);
      default:
        return new Auth0IdentityProvider(idpConfig);
    }
  };

  /**
   * @function
   * @static
   * @description A helper function that will fetch the IDP object based on context set using cookie AUTH0_IDP_CONTEXT_KEY.
   * @param {string} tenantOrDomain The Auth0-based ID of the tenant of the IDP
   * @param {object} [req] Not required on browser, otherwise the NodeJS, ExpressJS request that holds IDP context
   * @returns {object} An object representing the Identity Provider for the current context
   */
  static getIdpFromContext = (tenantOrDomain, req = {}) => {
    console.log(`getIdpFromContext starts for tenant => ${tenantOrDomain}`);
    const idpHelper = new Auth0SupportedIdps({ tenantOrDomain });
    const activeIdp = idpHelper.supportedIdps.filter(idp => idp.isSigninActive({ req }));
    if (!activeIdp || activeIdp.length === 0) {
      throw new Error('Could not fetch IDP from context because no context exists.');
    }

    if (activeIdp.length > 1) {
      const activeIds = activeIdp.map(idp => idp.id);
      logger.warn(`getIdpFromContext found multiple active IDPs (this is a bug), using first instance. Active => ${activeIds.join(',')}`);
    }
    const active = activeIdp.pop();
    console.log(`getIdpFromContext found active IDP => ${active.id}`);

    return idpHelper.getIdpById(active.id);
  };

  /**
   * @function
   * @description A helper function that will fetch the IDP object for a specific ID (Auth0-based ID)
   * @param {string} idpId The Auth0-based ID of the Identity Provider to fetch
   * @returns {object} An object representing the Identity Provider for `idpId` (see example above)
   */
  getIdpById = (idpId) => {
    console.log(`getIdpById => ${idpId}`);
    return this.supportedIdps.find(idp => idp.id === idpId);
  };

  /**
   * @function
   * @description A helper function that will fetch the IDP object for a specific ID (Auth0-based ID)
   * @param {string} userProfile The Auth0-based user profile for an identity provider
   * @param {string} userProfile.sub The normalized user ID for an identity provider
   * @returns {object} An object representing the Identity Provider for `idpId` (see example above)
   */
  getIdpFromUserProfile = (userProfile) => {
    console.log(`getIdpFromUserProfile => ${JSON.stringify(userProfile)}`);
    const { sub } = userProfile;

    if (!sub) {
      throw new Error('Cannot fetch IdP object because userProfile does not contain the "sub" property');
    }
    const idpId = Auth0IdentityProvider.getIdpIdFromUserId(userProfile.sub);
    return this.getIdpById(idpId);
  };

  /**
   * @function
   * @description A helper function that will fetch the friendly name for a specific ID (Auth0-based ID)
   * @param {string} idpId The Auth0-based ID of the Identity Provider to fetch
   * @returns {string} A string representing the name of the Identity Provider for `idpId`
   */
  getIdpNameById = (idpId) => {
    return this.getIdpById(idpId)?.name;
  };

  /**
   * @function
   * @description A helper function that will fetch the logo icon URL for a specific ID (Auth0-based ID)
   * @param {string} idpId The Auth0-based ID of the Identity Provider to fetch
   * @returns {string} A string representing the logo icon URL of the Identity Provider for `idpId`
   */
  getIdpLogoUrlById = (idpId) => {
    return this.getIdpById(idpId)?.logoUrl;
  };

  /**
   * @function
   * @description A helper function that will fetch the boolean indicating if the IdP login should run in a popup window or using full page redirects for a specific ID (Auth0-based ID)
   * @param {string} idpId The Auth0-based ID of the Identity Provider to fetch
   * @returns {boolean} A boolean representing the IdP's loginWithPopup value.
   */
  getIdpLoginWithPopupById = (idpId) => {
    return this.getIdpById(idpId)?.loginWithPopup;
  };

  /**
   * @function
   * @description A helper function starts the signin flow for an IdP (login flow differs based on IDP settings)
   * @param {string} idpId The Auth0-based ID of the Identity Provider for which to start the signin flow
   * @param {string} [appRedirectUrl] The Auctane app URL to which Auth0 should redirect. This arg is an override. If none is supplied, this will default to using this._appLoginHandlerUrl
   */
  startIdpLogin = async (idpId, appRedirectUrl = null) => {
    const idp = this.getIdpById(idpId);
    console.log(`start async idp login for ${idp.id}`);
    await idp.startIdpLoginHandler(appRedirectUrl || this._appLoginHandlerUrl);
  };

  /**
   * @function
   * @description A helper function starts the signin flow for an IdP (login flow differs based on IDP settings) for purposes of creating a new SP user
   * @param {string} options User onboarding options
   * @param {string} options.idpId The Auth0-based ID of the Identity Provider for which to start the signin flow
   * @param {string} [options.ipAddress] Optional. Indicate the IP address from where the SSO signup button was clicked.
   * @param {string} [options.registrationChannel] The channel used for registration. Not persisted, only used to bypass security checks where use of captcha is not possible. Omitting this value subjects onboarding to captcha. See {@link https://app.launchdarkly.com/default/production/features/enable-captcha-validation-for-ship-station-registration-channel/targeting}
   * @param {string} [options.ioBlackBoxToken] Anti-fraud Iovation BlackBox token. See SS marketing site for example generation
   * @param {string} [options.captchaToken] The captcha token is required if the `registrationChannel` is not present in the following LD flag. See {@link https://app.launchdarkly.com/default/production/features/enable-captcha-validation-for-ship-station-registration-channel/targeting}. Currently, this token must be Google reCaptcha v3 type.
   * @param {string} [options.hubSpotCookie] The optional tracking cookie used for HubSpot correlation.
   * @param {string} [options.requestedLanguage] Optional. If missing, language will be inferred by data in the IDP profile
   * @param {string} [options.appRedirectUrl] The Auctane app URL to which Auth0 should redirect. This arg is an override. If none is supplied, this will default to using this._appLoginHandlerUrl
   */
  startIdpOnboardingLogin = async (options) => {
    const idp = this.getIdpById(options.idpId);
    console.log(`start async idp login for onboarding for ${idp.id}`);
    options.appRedirectUrl = options.appRedirectUrl || this._appLoginHandlerUrl;
    await idp.startIdpOnboardingLoginHandler(options);
  };
}

export default Auth0SupportedIdps;
